// frontend/src/pages/Compare.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { Typography, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import SportsBasketballIcon from "@mui/icons-material/SportsBasketball";
import { toast } from "react-toastify";
import "../assets/players.css"; // Import custom CSS for styling
import "../assets/compare.css";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Utils/Loader";
import PageContainer from "./PageContainer";
import CompareCard from "../components/Compare/CompareCard";

function Compare() {
  const navigate = useNavigate();
  const location = useLocation();
  const [players, setPlayers] = useState([]);
  const [selectedPlayers, setSelectedPlayers] = useState([]);
  const [playersToCompare, setPlayersToCompare] = useState([]);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [topPlayers, setTopPlayers] = useState([]);
  const [topLoading, setTopLoading] = useState(true);
  const [topError, setTopError] = useState(null);

  useEffect(() => {
    // Fetch player list
    axios
      .get("/api/players/compare-list", { withCredentials: true })
      .then((response) => {
        setPlayers(response.data);
        setLoading(false);
      })
      .catch(() => {
        toast.error("Failed to load players");
        setLoading(false);
      });
  }, []);

  // Fetch top 10 high school players by xfactor for preview
  useEffect(() => {
    axios
      .get(`/api/players/?sortBy=xfactor`, { withCredentials: true })
      .then((response) => {
        const list = response.data || [];
        setTopPlayers(list.slice(0, 10));
        setTopError(null);
      })
      .catch((err) => {
        console.error(err);
        setTopError("Failed to load top players");
      })
      .finally(() => setTopLoading(false));
  }, []);

  // If navigated with ?playerId or ?playerName, preselect that player
  useEffect(() => {
    if (!players || players.length === 0) return; // wait for list
    const params = new URLSearchParams(location.search);
    const qId = params.get("playerId");
    const qName = params.get("playerName");
    if (!qId && !qName) return;

    // Avoid duplicates
    const already = new Set(selectedPlayers.map((p) => p.player_id));
    let match = null;
    if (qId) {
      match = players.find((p) => String(p.player_id) === String(qId));
    }
    if (!match && qName) {
      const lname = qName.toLowerCase();
      match = players.find(
        (p) => (p.player_name || "").toLowerCase() === lname
      );
      // if exact not found, try contains
      if (!match) {
        match = players.find((p) =>
          (p.player_name || "").toLowerCase().includes(lname)
        );
      }
    }
    if (match && !already.has(match.player_id)) {
      setSelectedPlayers((prev) => [...prev, match]);
      // Also show a comparison immediately if none started
      setPlayersToCompare((prev) => (prev.length ? prev : [match]));
    }
  }, [players, location.search]);

  const filteredSuggestions = useMemo(() => {
    if (!searchTerm) return [];
    const q = searchTerm.toLowerCase();
    const selectedIds = new Set(selectedPlayers.map((p) => p.player_id));
    return players
      .filter(
        (p) =>
          !selectedIds.has(p.player_id) &&
          (p.player_name || "").toLowerCase().includes(q)
      )
      .slice(0, 10);
  }, [searchTerm, players, selectedPlayers]);

  const handleAddPlayer = (player) => {
    if (selectedPlayers.length >= 10) {
      toast.warning("Maximum 10 players can be compared at once");
      return;
    }
    setSelectedPlayers((prev) => [...prev, player]);
    setSearchTerm("");
  };

  // Instantly compare a single player (used by preview cards)
  const handleInstantCompare = (player) => {
    setSelectedPlayers([player]);
    setComparing(true);
    const sortedPlayers = [player];
    setTimeout(() => {
      setPlayersToCompare(sortedPlayers);
      setComparing(false);
    }, 300);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (filteredSuggestions.length > 0) {
        handleAddPlayer(filteredSuggestions[0]);
      } else {
        toast.info("No matching player");
      }
    }
  };

  const handleSubmit = () => {
    if (selectedPlayers.length === 0) {
      toast.error("Please select at least one player");
      return;
    }

    setComparing(true);

    const sortedPlayers = [...selectedPlayers].sort(
      (a, b) => b.xfactor - a.xfactor
    );

    setTimeout(() => {
      setPlayersToCompare(sortedPlayers);
      setComparing(false);
    }, 1000);
  };

  // Handle removal of a player from the comparison
  const handleRemovePlayer = (playerId) => {
    // Remove from playersToCompare
    const updatedPlayersToCompare = playersToCompare.filter(
      (player) => player.player_id !== playerId
    );
    setPlayersToCompare(updatedPlayersToCompare);

    // Also remove from selectedPlayers to keep them in sync
    const updatedSelectedPlayers = selectedPlayers.filter(
      (player) => player.player_id !== playerId
    );
    setSelectedPlayers(updatedSelectedPlayers);
  };

  // Reset page to default view
  const handleReset = () => {
    setSelectedPlayers([]);
    setPlayersToCompare([]);
    setSearchTerm("");
    setComparing(false);
  };

  if (loading) {
    return <Loader />;
  }

  return (
    <PageContainer>
      <div className="compare-page">
        {/* Basketball Court Header */}
        <div className="compare-court-header">
          <div className="compare-court-circle">
            <SportsBasketballIcon className="compare-basketball-icon" />
          </div>
          <Typography variant="h2" className="compare-main-title">
            Player Comparison
          </Typography>
          <p className="compare-subtitle">
            Compare elite high school basketball talent side by side
          </p>
        </div>

        {/* Search Command Center */}
        <div className="compare-search-command">
          <div className="compare-search-glass">
            <div className="compare-search-input-wrapper">
              <SearchIcon className="compare-search-icon" />
              <input
                type="text"
                placeholder="Search for players..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                className="compare-search-input"
              />
              {searchTerm && (
                <IconButton
                  className="compare-search-clear"
                  onClick={() => setSearchTerm("")}
                  size="small"
                >
                  <ClearIcon />
                </IconButton>
              )}
            </div>
          </div>

          {/* Animated Search Suggestions - moved outside glass container */}
          {searchTerm && filteredSuggestions.length > 0 && (
            <div className="compare-suggestions-container">
              <ul className="compare-suggestions-list">
                {filteredSuggestions.map((p, index) => (
                  <li
                    key={p.player_id}
                    className="compare-suggestion-item"
                    onClick={() => handleAddPlayer(p)}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="compare-suggestion-avatar">
                      {p.player_name?.charAt(0) || "?"}
                    </div>
                    <div className="compare-suggestion-info">
                      <span className="compare-suggestion-name">
                        {p.player_name}
                      </span>
                      <span className="compare-suggestion-meta">
                        {p.position} • {p.class}
                      </span>
                    </div>
                    <div className="compare-suggestion-xfactor">
                      {Number.isFinite(parseFloat(p.xfactor))
                        ? Math.round(parseFloat(p.xfactor))
                        : "--"}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Selected Players Roster */}
          {selectedPlayers.length > 0 && (
            <div className="compare-roster">
              <div className="compare-roster-title">
                <SportsBasketballIcon className="compare-roster-icon" />
                Players Selected ({selectedPlayers.length}/10)
              </div>
              <div className="compare-roster-players">
                {selectedPlayers.map((p, index) => (
                  <div
                    key={p.player_id}
                    className="compare-roster-player"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="compare-roster-player-avatar">
                      {p.player_name?.charAt(0) || "?"}
                    </div>
                    <span className="compare-roster-player-name">
                      {p.player_name}
                    </span>
                    <button
                      className="compare-roster-remove"
                      onClick={() => handleRemovePlayer(p.player_id)}
                      aria-label={`Remove ${p.player_name}`}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="compare-actions">
            <button
              className="compare-tip-off-button"
              onClick={handleSubmit}
              disabled={selectedPlayers.length === 0}
            >
              Start Comparison
            </button>
            {(playersToCompare.length > 0 || selectedPlayers.length > 0) && (
              <button
                className="compare-clear-court-button"
                onClick={handleReset}
              >
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Game Loading Animation */}
        {comparing && (
          <div className="compare-game-loading">
            <Loader fixed={false} />
          </div>
        )}

        {/* Comparison Results */}
        {!comparing && playersToCompare.length > 0 && (
          <div className="compare-results-arena">
            <CompareCard
              similarPlayers={playersToCompare}
              onRemovePlayer={handleRemovePlayer}
            />
          </div>
        )}

        {/* Elite Players Showcase - shown when no selection/search yet */}
        {selectedPlayers.length === 0 &&
          playersToCompare.length === 0 &&
          !searchTerm && (
            <div className="compare-elite-showcase">
              <div className="compare-showcase-header">
                <h2 className="compare-showcase-title">Elite Prospects</h2>
                <p className="compare-showcase-subtitle">
                  Tap any player to start your comparison
                </p>
              </div>
              {topLoading ? (
                <div className="compare-showcase-loading">
                  <div className="compare-basketball-loading">
                    <SportsBasketballIcon />
                  </div>
                  <p>Loading elite prospects...</p>
                </div>
              ) : topError ? (
                <div className="compare-showcase-error">
                  <SportsBasketballIcon className="compare-error-icon" />
                  <p>{topError}</p>
                </div>
              ) : (
                <div className="compare-elite-grid">
                  {topPlayers.map((p, idx) => (
                    <div
                      key={p.player_id || idx}
                      className="compare-elite-card"
                      onClick={() => handleInstantCompare(p)}
                      style={{ animationDelay: `${idx * 0.1}s` }}
                    >
                      <div className="compare-elite-rank">#{idx + 1}</div>
                      <div className="compare-elite-image">
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt={p.player_name}
                            onError={(e) => {
                              e.target.onerror = null;
                              const backupImages = [
                                "https://d3mvy6uwazuxyu.cloudfront.net/back-b-2.png",
                                "https://d3mvy6uwazuxyu.cloudfront.net/back-b-3.png",
                                "https://d3mvy6uwazuxyu.cloudfront.net/back-b-4.png",
                              ];
                              const randomBackup =
                                backupImages[
                                  Math.floor(
                                    Math.random() * backupImages.length
                                  )
                                ];
                              e.target.src = randomBackup;
                            }}
                          />
                        ) : (
                          <div className="compare-elite-placeholder">
                            <SportsBasketballIcon />
                          </div>
                        )}
                        <div className="compare-elite-overlay">
                          <img
                            src="/xsmlgsm.PNG"
                            alt="XSM Logo"
                            className="compare-elite-play-icon"
                          />
                        </div>
                      </div>
                      <div className="compare-elite-info">
                        <div className="compare-elite-info-top">
                          <h3 className="compare-elite-name">
                            {p.player_name}
                          </h3>
                          <div className="compare-elite-details">
                            {p.position && (
                              <span className="compare-elite-position">
                                {p.position}
                              </span>
                            )}
                            {p.class && (
                              <span className="compare-elite-class">
                                {p.class}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="compare-elite-info-bottom">
                          <div className="compare-elite-xfactor">
                            <span className="compare-elite-xfactor-label">
                              X-Factor
                            </span>
                            <span className="compare-elite-xfactor-score">
                              {Number.isFinite(parseFloat(p.xfactor))
                                ? Math.round(parseFloat(p.xfactor))
                                : "--"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
      </div>
    </PageContainer>
  );
}

export default Compare;
